/**
 * The app loaded but the API did not answer.
 *
 * Not a route. `Screen` presents this in place of the body of whatever screen
 * is open, keeping the header and the nav, and leaves that screen mounted
 * underneath with its data intact — so when the origin comes back, returning is
 * instant rather than a reload.
 *
 * screens.json is explicit about what may appear here: the two keyed strings
 * and one button. No status code, no hostname, and never the words tunnel,
 * proxy or relay. The user is not operating the internals, and none of those
 * would change what they do next.
 */
import { Button } from '@/components/primitives'
import { t } from '@/lib/copy'
import { useOriginOutage } from '@/lib/origin'

export function ServiceUnavailableScreen() {
  const { retry, retrying, canRetry } = useOriginOutage()
  const state = retrying ? 'retrying' : 'ready'

  return (
    // `data-screen` is the same id the bridge reports, so an instruction about
    // this screen and a query for it in the render harness resolve identically.
    <div className="state-block" role="status" aria-live="polite" data-screen="serviceUnavailable" data-state={state}>
      <h2 className="state-title">{t('serviceUnavailable.title')}</h2>
      <p className="state-body">{t('serviceUnavailable.body')}</p>
      {/*
        Offered only where re-issuing is known to be safe. On the two codes that
        mean "handed to the mini and never answered", the request may already
        have been applied — retrying there imports the same statement twice, so
        the contract's answer is no button at all (globalRules.noAutoRetry).
      */}
      {canRetry ? (
        <Button variant="primary" onClick={retry} busy={retrying}>
          {t('cta.retry')}
        </Button>
      ) : null}
    </div>
  )
}
